import { sharedPoll, stopPoll } from '@/command/poll'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { servicesMonitorApi } from '@/api/servicesMonitor'
import {
  monitorApi,
  type MonitorBucket,
  type MonitorCheck,
  type MonitorCron,
  type MonitorEf,
  type MonitorIntegration,
  type MonitorDiffRow,
  type MonitorScanDiff,
  type MonitorSnapshot,
  type MonitorStatus,
} from '@/api/monitor'
import HealthPage from '@/pages/health/HealthPage'
import MonitorBranchesTab from '@/pages/MonitorBranchesTab'
import SystemFaultsTab from '@/pages/services/SystemFaultsTab'
import { MonitorReleaseAlerts } from '@/components/ReleaseAlerts'
import type { CronHealthRow, MetricsSeriesRow, OrgQuotaUsageRow, ServiceAlertRow } from '@/types'

const FEATURE_IMPACT: Array<{ feature: string; services: string[] }> = [
  { feature: 'WhatsApp שליחה / קבלה', services: ['WhatsApp Service', 'Supabase DB', 'pg_net'] },
  { feature: 'CRM / אנשי קשר', services: ['Supabase DB'] },
  { feature: 'תשלומים / SUMIT', services: ['Supabase DB', 'Edge Functions', 'pg_net'] },
  { feature: 'אוטומציות / Journeys', services: ['Cron', 'Edge Functions', 'WhatsApp Service'] },
  { feature: 'AI Credits', services: ['Edge Functions', 'Supabase DB'] },
  { feature: 'טפסים / רישום לכיתות', services: ['Supabase DB', 'Edge Functions'] },
  { feature: 'מיילים', services: ['Edge Functions', 'pg_net'] },
]
const CRON_FEATURE: Record<string, string> = {
  'collect-cron-health': 'מוניטור שירותים', 'collect-platform-metrics': 'מוניטור DB',
  'snapshot-org-quota-usage': 'מכסות ארגונים', 'evaluate-service-alerts': 'התראות מערכת',
  'wa-canary': 'WhatsApp canary', 'rollup-system-metrics': 'מדדי מערכת',
}
const QUOTA_METRICS = ['contacts', 'whatsapp_devices', 'ai_credits_monthly'] as const
const HOURLY_BUCKET_STALE_MS = 90 * 60 * 1000

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

const TABS = [
  { id: 'modules', label: 'מודולים' },
  { id: 'functions', label: 'פונקציות' },
  { id: 'integrations', label: 'אינטגרציות' },
  { id: 'cron', label: 'Cron' },
  { id: 'infra', label: 'תשתית' },
  { id: 'branches', label: 'ענפי Supabase' },
  { id: 'checks', label: 'בדיקות נתונים' },
  { id: 'faults', label: 'תקלות והתראות' },
] as const

type TabId = (typeof TABS)[number]['id']

const PROJECT_REF: Record<string, string> = {
  dgxn: 'dgxnnwnugdxzeopleera',
  mzalz: 'mzalzjtsyrjycaxolldv',
}

function statusPaint(status: MonitorStatus | undefined): { color: string; label: string } {
  if (status === 'red') return { color: '#f87171', label: 'אדום' }
  if (status === 'yellow') return { color: '#fbbf24', label: 'צהוב' }
  if (status === 'green') return { color: '#34d399', label: 'ירוק' }
  if (status === 'off') return { color: '#6b7280', label: 'כבוי' }
  return { color: '#9ca3af', label: 'אין נתונים' }
}

function StatusPill({ status }: { status: MonitorStatus | undefined }) {
  const paint = statusPaint(status)
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: paint.color }}>
      <span className="inline-block h-2 w-2 rounded-full" style={{ background: paint.color }} />
      {paint.label}
    </span>
  )
}

function textOf(value: unknown): string {
  if (value == null || value === '') return '—'
  if (typeof value === 'string' || typeof value === 'number') return String(value)
  if (Array.isArray(value)) {
    if (value.length === 0) return '—'
    return value.map((item) => textOf(item)).join(' · ')
  }
  if (typeof value === 'object') {
    const row = value as { title_he?: string; key?: string; status?: string; level?: string; value?: number }
    const name = row.title_he || row.key
    if (name) {
      const mark = row.status || row.level
      const amount = typeof row.value === 'number' ? ` ${row.value}` : ''
      return mark ? `${name}${amount} · ${mark}` : `${name}${amount}`
    }
  }
  return JSON.stringify(value)
}

function agoMinutes(iso: string | null | undefined): string {
  if (!iso) return 'אין נתונים'
  const ms = Date.now() - new Date(iso).getTime()
  if (!Number.isFinite(ms)) return 'אין נתונים'
  const mins = Math.max(0, Math.round(ms / 60000))
  if (mins < 60) return `לפני ${mins} דק׳`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `לפני ${hours} שע׳`
  const days = Math.round(hours / 24)
  if (days === 1) return 'לפני יום'
  if (days === 2) return 'לפני יומיים'
  return `לפני ${days} ימים`
}

function lastFinishedAt(snapshot: MonitorSnapshot | null): string | null {
  let best: string | null = null
  for (const scan of snapshot?.scans ?? []) {
    if (!scan.finished_at) continue
    if (!best || scan.finished_at > best) best = scan.finished_at
  }
  return best
}

function componentName(row: { function_name?: string; jobname?: string; provider?: string; key?: string; title_he?: string }): string {
  return row.function_name || row.jobname || row.provider || row.title_he || row.key || '—'
}

function efLogUrl(ef: MonitorEf): string | null {
  const name = ef.function_name || ef.key
  if (!name) return null
  const ref = PROJECT_REF[ef.project ?? 'dgxn'] ?? PROJECT_REF.dgxn
  return `https://supabase.com/dashboard/project/${ref}/functions/${encodeURIComponent(name)}/logs`
}

function evidenceOf(row: { evidence?: string | null; top_error?: string | null; error?: string | null; last_status?: string | null }): string {
  return row.evidence || row.top_error || row.error || row.last_status || '—'
}

function efTopError(row: MonitorEf): string | null {
  const errors = (row.errors_4xx_1h ?? 0) + (row.errors_5xx_1h ?? 0)
  return errors > 0 ? row.top_error || null : null
}

const DIFF_TYPE_LABEL: Record<string, string> = {
  edge_function: 'פונקציה', cron: 'Cron', integration: 'אינטגרציה', check: 'בדיקה',
}

function diffLine(row: MonitorDiffRow, group: 'worse' | 'better' | 'new' | 'gone'): string {
  const identity = `${row.key || '—'} (${DIFF_TYPE_LABEL[row.type ?? ''] || row.type || 'רכיב'} · ${row.project || '—'})`
  if (group === 'new') return `${identity}: חדש · ${statusPaint(row.status).label}`
  if (group === 'gone') return `${identity}: נעלם · היה ${statusPaint(row.status).label}`
  const before = row.from ?? row.prior_status
  const after = row.to ?? row.status
  return `${identity}: ${statusPaint(before).label} → ${statusPaint(after).label}`
}

function ScanDiff({ diff }: { diff: MonitorScanDiff }) {
  const groups = [
    { key: 'worse' as const, label: 'החמיר' },
    { key: 'better' as const, label: 'השתפר' },
    { key: 'new' as const, label: 'חדש' },
    { key: 'gone' as const, label: 'נעלם' },
  ]
  const hasChanges = groups.some(({ key }) => (diff[key]?.length ?? 0) > 0)
  return (
    <section className="mb-3 rounded-lg p-4" style={{ background: '#1a1d27', border: '1px solid #2d3148' }}>
      <h2 className="text-sm font-semibold mb-2">שינויים מהסריקה הקודמת</h2>
      {diff.error ? <p className="text-xs" style={{ color: '#f87171' }}>לא ניתן לטעון את השינויים: {diff.error}</p> : !hasChanges ? <p className="text-xs" style={{ color: '#9ca3af' }}>אין שינויים</p> : groups.map(({ key, label }) => {
        const rows = diff[key] ?? []
        if (rows.length === 0) return null
        return <div key={key} className="mb-3 last:mb-0"><h3 className="text-xs font-semibold mb-1">{label}</h3><ul className="space-y-1 text-xs" style={{ color: '#d1d5db' }}>{rows.map((row, index) => <li key={`${row.type}-${row.key}-${row.project}-${index}`}>{diffLine(row, key)}</li>)}</ul></div>
      })}
    </section>
  )
}

function fmtDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)} שנ׳`
  if (seconds < 3600) return `${Math.round(seconds / 60)} דק׳`
  if (seconds < 86400) return `${Math.round(seconds / 3600)} שע׳`
  return `${Math.round(seconds / 86400)} ימים`
}

function cronImpact(jobname: string): string {
  if (CRON_FEATURE[jobname]) return CRON_FEATURE[jobname]
  const match = Object.entries(CRON_FEATURE).find(([key]) => jobname.includes(key) || key.includes(jobname))
  if (match) return match[1]
  if (/wa|whatsapp/i.test(jobname)) return 'WhatsApp'
  if (/email|mail/i.test(jobname)) return 'מיילים'
  if (/metric|collect/i.test(jobname)) return 'מוניטור'
  return '—'
}

export default function MonitorPage() {
  const [params, setParams] = useSearchParams()
  const tab = (TABS.some((item) => item.id === params.get('tab')) ? params.get('tab') : 'modules') as TabId
  const [snapshot, setSnapshot] = useState<MonitorSnapshot | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [drawer, setDrawer] = useState<string | null>(null)
  const [scanNote, setScanNote] = useState<string | null>(null)
  const [scanBusy, setScanBusy] = useState(false)
  const [diff, setDiff] = useState<MonitorScanDiff | null>(null)
  const [activeRun, setActiveRun] = useState<string | null>(null)
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null)

  const load = useCallback(async () => {
    try {
      const next = await monitorApi.snapshot()
      setSnapshot(next)
      setError(null)
      if (next.running?.id) setActiveRun(next.running.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'snapshot failed')
      return false
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const poll = sharedPoll(load, activeRun ? 3000 : 30000, true)
    return () => stopPoll(poll)
  }, [load, activeRun])

  useEffect(() => {
    const capture = (event: Event) => { event.preventDefault(); setInstallPrompt(event as BeforeInstallPromptEvent) }
    const clear = () => setInstallPrompt(null)
    window.addEventListener('beforeinstallprompt', capture)
    window.addEventListener('appinstalled', clear)
    return () => { window.removeEventListener('beforeinstallprompt', capture); window.removeEventListener('appinstalled', clear) }
  }, [])


  useEffect(() => {
    if (!activeRun || !snapshot) return
    const row = snapshot.scans?.find((scan) => scan.id === activeRun)
    if (!row?.finished_at) return
    setActiveRun(null)
    setScanNote(`הסריקה הסתיימה · ${activeRun}`)
    void monitorApi.diff(activeRun).then(setDiff).catch((err: unknown) => {
      setDiff({ error: err instanceof Error ? err.message : 'diff failed' })
    })
  }, [snapshot, activeRun])

  async function scanNow() {
    if (snapshot?.running?.id || scanBusy) {
      setScanNote(`סריקה כבר רצה · ${snapshot?.running?.id ?? activeRun ?? ''}`)
      return
    }
    setScanBusy(true)
    setDiff(null)
    try {
      const result = await monitorApi.startFullScan()
      if (result.started === false) {
        setScanNote(`סריקה כבר רצה · ${result.run_id ?? ''}`)
        if (result.run_id) setActiveRun(result.run_id)
      } else if (result.run_id) {
        setActiveRun(result.run_id)
        setScanNote(`רץ · ${result.run_id}`)
      } else {
        setScanNote(result.error ?? 'הסריקה לא התחילה')
      }
      await load()
    } finally {
      setScanBusy(false)
    }
  }

  const runningSteps = snapshot?.running?.steps ?? snapshot?.scans?.find((scan) => scan.id === activeRun)?.steps

  const scanRunning = Boolean(snapshot?.running?.id || activeRun)

  return (
    <div dir="rtl" className="min-h-full w-full min-w-0 max-w-full overflow-x-hidden text-white" style={{ background: '#0f1117' }}>
      <header className="flex flex-col gap-3 mb-4 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold">מוניטור</h1>
          <p className="text-sm mt-1 flex flex-wrap gap-3" style={{ color: '#9ca3af' }}>
            <StatusCount color="#f87171" n={snapshot?.totals?.red} label="אדום" />
            <StatusCount color="#fbbf24" n={snapshot?.totals?.yellow} label="צהוב" />
            <StatusCount color="#9ca3af" n={snapshot?.totals?.unknown} label="אין נתונים" />
            <StatusCount color="#34d399" n={snapshot?.totals?.green} label="ירוק" />
            <span>נסרק {agoMinutes(lastFinishedAt(snapshot))}</span>
            {scanRunning && <span style={{ color: '#fbbf24' }}>סריקה רצה</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {installPrompt && (
            <button type="button" className="min-h-10 rounded-lg px-4 text-sm font-medium" style={{ background: '#1a1d27', border: '1px solid #6366f1' }} onClick={() => void installPrompt.prompt().then(() => installPrompt.userChoice).then(() => setInstallPrompt(null))}>
              התקנת האפליקציה
            </button>
          )}
          <button
            type="button"
            onClick={() => void load()}
            className="min-h-10 rounded-lg px-4 text-sm font-medium"
            style={{ background: '#1a1d27', color: '#e5e7eb', border: '1px solid #2d3148' }}
          >
            רענן
          </button>
          <button
            type="button"
            onClick={() => void scanNow()}
            className="min-h-10 rounded-lg px-4 text-sm font-medium text-white"
            style={{ background: '#4f46e5' }}
          >
            {scanRunning ? 'סריקה רצה' : 'סרוק עכשיו'}
          </button>
        </div>
      </header>

      {error && <p className="text-sm mb-3" style={{ color: '#f87171' }}>{error}</p>}
      {scanNote && <p className="text-xs mb-2" style={{ color: '#d1d5db' }}>{scanNote}</p>}
      {runningSteps && (
        <pre className="text-[11px] mb-3 w-full min-w-0 max-w-full overflow-x-auto rounded-lg p-3" style={{ background: '#1a1d27', color: '#9ca3af' }}>
          {JSON.stringify(runningSteps, null, 2)}
        </pre>
      )}
      {diff != null && (
        <ScanDiff diff={diff} />
      )}

      {(snapshot?.blind_spots?.length ?? 0) > 0 && (
        <section className="rounded-xl p-4 mb-4" style={{ background: '#1a1d27', border: '1px solid #2d3148' }}>
          <h2 className="text-sm font-semibold mb-2">מה המוניטור לא רואה</h2>
          <ul className="text-xs space-y-1" style={{ color: '#9ca3af' }}>
            {snapshot?.blind_spots?.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </section>
      )}

      <div className="flex w-full min-w-0 max-w-full gap-2 overflow-x-auto pb-3 mb-4">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className="min-h-10 shrink-0 rounded-lg px-3 text-sm"
            style={{
              background: tab === item.id ? '#4f46e5' : '#1a1d27',
              color: tab === item.id ? '#fff' : '#9ca3af',
            }}
            onClick={() => {
              const next = new URLSearchParams(params)
              next.set('tab', item.id)
              setParams(next)
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {loading && !snapshot && <p className="text-sm" style={{ color: '#9ca3af' }}>טוען…</p>}
      {tab === 'modules' && snapshot && <ModulesTab snapshot={snapshot} onOpen={setDrawer} />}
      {tab === 'functions' && snapshot && <FunctionsTab rows={snapshot.efs ?? []} />}
      {tab === 'integrations' && snapshot && <IntegrationsTab rows={snapshot.integrations ?? []} />}
      {tab === 'cron' && snapshot && <CronTab rows={snapshot.cron ?? []} />}
      {tab === 'infra' && snapshot && <InfraTab infra={snapshot.infra} />}
      {tab === 'branches' && <MonitorBranchesTab refreshKey={lastFinishedAt(snapshot)} scanBusy={scanRunning || scanBusy} />}
      {tab === 'checks' && snapshot && <ChecksTab rows={snapshot.checks ?? []} />}
      {tab === 'faults' && <FaultsTab />}

      {drawer && snapshot && <Drawer code={drawer} snapshot={snapshot} onClose={() => setDrawer(null)} />}
    </div>
  )
}

function StatusCount({ color, n, label }: { color: string; n: number | undefined; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="inline-block h-2 w-2 rounded-full" style={{ background: color }} />
      {n ?? '—'} {label}
    </span>
  )
}

function ModulesTab({ snapshot, onOpen }: { snapshot: MonitorSnapshot; onOpen: (code: string) => void }) {
  return (
    <div className="w-full min-w-0 max-w-full overflow-x-auto rounded-xl" style={{ border: '1px solid #2d3148' }}>
      <table className="w-full min-w-[720px] text-right text-sm">
        <thead style={{ color: '#6b7280' }}>
          <tr>
            {['מודול', 'סטטוס', 'EFs', 'Crons', 'אינטגרציות', 'בדיקות', 'תקלה אחרונה', '24h שגיאות'].map((h) => (
              <th key={h} className="px-3 py-2 font-medium">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(snapshot.modules ?? []).map((row) => (
            <tr
              key={row.module_code}
              className="cursor-pointer"
              style={{ borderTop: '1px solid #2d3148' }}
              onClick={() => onOpen(row.module_code)}
            >
              <td className="px-3 py-2.5">{row.name_he || row.module_code}</td>
              <td className="px-3 py-2.5"><StatusPill status={row.status} /></td>
              <td className="px-3 py-2.5 font-mono">{row.efs_ok ?? '—'}/{row.efs_total ?? '—'}</td>
              <td className="px-3 py-2.5 font-mono">{row.crons_ok ?? '—'}/{row.crons_total ?? '—'}</td>
              <td className="px-3 py-2.5 text-xs max-w-[220px] whitespace-normal">{textOf(row.integrations)}</td>
              <td className="px-3 py-2.5 text-xs max-w-[220px] whitespace-normal">{textOf(row.checks)}</td>
              <td className="px-3 py-2.5 text-xs">{row.last_fault_title || row.last_fault || '—'}{row.last_fault_at ? <span className="block" style={{ color: '#6b7280' }}>{agoMinutes(row.last_fault_at)}</span> : null}</td>
              <td className="px-3 py-2.5 font-mono">{row.errors_24h ?? '—'}</td>
            </tr>
          ))}
          <BucketRow label="ליבה" code="platform" bucket={snapshot.platform} onOpen={onOpen} />
          <BucketRow label="לא משויך" code="unmapped" bucket={snapshot.unmapped} onOpen={onOpen} />
        </tbody>
      </table>
    </div>
  )
}

function BucketRow({ label, code, bucket, onOpen }: { label: string; code: string; bucket?: MonitorBucket; onOpen: (code: string) => void }) {
  return (
    <tr className="cursor-pointer" style={{ borderTop: '1px solid #2d3148' }} onClick={() => onOpen(code)}>
      <td className="px-3 py-2.5">{label}</td>
      <td className="px-3 py-2.5"><StatusPill status={bucket?.status ?? 'unknown'} /></td>
      <td className="px-3 py-2.5 font-mono">{bucket?.count_efs ?? '—'}</td>
      <td className="px-3 py-2.5 font-mono">{bucket?.count_crons ?? '—'}</td>
      <td className="px-3 py-2.5">—</td>
      <td className="px-3 py-2.5">—</td>
      <td className="px-3 py-2.5 text-xs">{bucket?.last_fault_title || '—'}{bucket?.last_fault_at ? <span className="block" style={{ color: '#6b7280' }}>{agoMinutes(bucket.last_fault_at)}</span> : null}</td>
      <td className="px-3 py-2.5">—</td>
    </tr>
  )
}

function FunctionsTab({ rows }: { rows: MonitorEf[] }) {
  const sorted = useMemo(
    () => [...rows].sort((a, b) => (b.error_pct ?? -1) - (a.error_pct ?? -1)),
    [rows],
  )
  return (
    <div className="w-full min-w-0 max-w-full overflow-x-auto rounded-xl" style={{ border: '1px solid #2d3148' }}>
      <table className="w-full min-w-[880px] text-right text-xs">
        <thead style={{ color: '#6b7280' }}>
          <tr>
            {['פונקציה', 'מודול', 'סטטוס', 'קריאות/שעה', '5xx', 'שגיאה %', 'שגיאה אחרונה', 'verify_jwt', 'לוג'].map((h) => (
              <th key={h} className="px-2 py-2 font-medium">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const href = efLogUrl(row)
            return (
              <tr key={`${row.project}-${row.function_name}`} style={{ borderTop: '1px solid #2d3148' }}>
                <td className="px-2 py-2">{componentName(row)}</td>
                <td className="px-2 py-2">{row.module_code}</td>
                <td className="px-2 py-2"><StatusPill status={row.status} /></td>
                <td className="px-2 py-2 font-mono">{row.invocations_1h ?? '—'}</td>
                <td className="px-2 py-2 font-mono">{row.errors_5xx_1h ?? '—'}</td>
                <td className="px-2 py-2 font-mono">{row.error_pct ?? '—'}</td>
                <td className="px-2 py-2">{efTopError(row) || '—'}</td>
                <td className="px-2 py-2">{row.verify_jwt == null ? '—' : row.verify_jwt ? 'כן' : 'לא'}</td>
                <td className="px-2 py-2">{href ? <a href={href} target="_blank" rel="noreferrer" className="underline">לוג</a> : '—'}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function IntegrationsTab({ rows }: { rows: MonitorIntegration[] }) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {rows.map((row) => (
        <article key={`${row.module_code}-${row.provider}`} className="rounded-xl p-4" style={{ background: '#1a1d27', border: '1px solid #2d3148' }}>
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold">{componentName(row)}</h3>
            <StatusPill status={row.status} />
          </div>
          <p className="text-xs mt-2" style={{ color: '#9ca3af' }}>
            מודול {row.module_code ?? '—'} · השהיה {row.latency_ms ?? '—'}ms · ארגונים {row.orgs ?? '—'}
          </p>
          <p className="text-xs mt-1" style={{ color: '#d1d5db' }}>{evidenceOf(row)}</p>
        </article>
      ))}
    </div>
  )
}

function CronTab({ rows }: { rows: MonitorCron[] }) {
  return (
    <div className="w-full min-w-0 max-w-full overflow-x-auto rounded-xl" style={{ border: '1px solid #2d3148' }}>
      <table className="w-full min-w-[800px] text-right text-xs">
        <thead style={{ color: '#6b7280' }}>
          <tr>
            {['עבודה', 'מודול', 'סטטוס', 'תזמון', 'ריצה אחרונה', 'כשלים 7 ימים', 'JWT קשיח', 'מה מושפע'].map((h) => (
              <th key={h} className="px-2 py-2 font-medium">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={componentName(row)} style={{ borderTop: '1px solid #2d3148' }}>
              <td className="px-2 py-2">{componentName(row)}</td>
              <td className="px-2 py-2">{row.module_code}</td>
              <td className="px-2 py-2"><StatusPill status={row.status} /></td>
              <td className="px-2 py-2 font-mono">{row.schedule}</td>
              <td className="px-2 py-2">{row.last_status || '—'}</td>
              <td className="px-2 py-2 font-mono">{row.fails_7d ?? '—'}</td>
              <td className="px-2 py-2">{row.hardcoded_jwt ? 'כן' : 'לא'}</td>
              <td className="px-2 py-2">{cronImpact(row.jobname ?? '')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function InfraTab({ infra }: { infra: MonitorSnapshot['infra'] }) {
  const rls = infra?.rls_off_dgxn
  const rlsCount = Array.isArray(rls) ? rls.length : rls == null ? null : textOf(rls)
  return (
    <div className="space-y-4">
      <div className="rounded-xl p-4 text-sm" style={{ background: '#1a1d27', border: '1px solid #2d3148' }}>
        <p>advisors-at: {infra?.advisors_at ?? 'אין נתונים'}</p>
        <p className="mt-1">טבלאות בלי RLS (dgxn): {rlsCount ?? 'אין נתונים'}</p>
        <p className="mt-1" style={{ color: '#9ca3af' }}>חיבורים {infra?.db_conns ?? '—'} · שאילתות איטיות {infra?.slow_queries ?? '—'}</p>
      </div>
      <HealthPage />
    </div>
  )
}

function ChecksTab({ rows }: { rows: MonitorCheck[] }) {
  return (
    <div className="w-full min-w-0 max-w-full overflow-x-auto rounded-xl" style={{ border: '1px solid #2d3148' }}>
      <table className="w-full min-w-[720px] text-right text-sm">
        <thead style={{ color: '#6b7280' }}>
          <tr>
            {['בדיקה', 'מודול', 'סטטוס', 'ערך', 'סף אזהרה', 'סף קריטי', 'ראיה'].map((h) => (
              <th key={h} className="px-3 py-2 font-medium">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} style={{ borderTop: '1px solid #2d3148' }}>
              <td className="px-3 py-2">{row.title_he || row.key}</td>
              <td className="px-3 py-2">{row.module_code}</td>
              <td className="px-3 py-2"><StatusPill status={row.status} /></td>
              <td className="px-3 py-2 font-mono">{row.value ?? '—'}</td>
              <td className="px-3 py-2 font-mono">{row.warn_at ?? '—'}</td>
              <td className="px-3 py-2 font-mono">{row.crit_at ?? '—'}</td>
              <td className="px-3 py-2 text-xs">{row.evidence || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function FaultsTab() {
  const [alerts, setAlerts] = useState<ServiceAlertRow[]>([])
  const [quota, setQuota] = useState<OrgQuotaUsageRow[]>([])
  const [cron, setCron] = useState<CronHealthRow[]>([])
  const [series, setSeries] = useState<MetricsSeriesRow[]>([])
  useEffect(() => {
    void Promise.all([servicesMonitorApi.alerts(true), servicesMonitorApi.quotaUsage(), servicesMonitorApi.cronHealth(), servicesMonitorApi.dbSeries(), servicesMonitorApi.waSeries()]).then(([a, q, c, db, wa]) => {
      setAlerts(a.data); setQuota(q.data); setCron(c.data); setSeries([...db.data, ...wa.data])
    })
  }, [])
  const newestMetric = series.reduce<number | null>((best, row) => {
    const value = Date.parse(row.bucket_hour)
    return Number.isFinite(value) && (best == null || value > best) ? value : best
  }, null)
  const metricStale = newestMetric == null || Date.now() - newestMetric > HOURLY_BUCKET_STALE_MS
  const quotaByOrg = useMemo(() => {
    const map = new Map<string, { name: string; rows: OrgQuotaUsageRow[]; share: number | null }>()
    for (const row of quota) {
      if (!QUOTA_METRICS.includes(row.metric as (typeof QUOTA_METRICS)[number])) continue
      const current = map.get(row.org_id) ?? { name: row.org_name ?? row.org_id.slice(0, 8), rows: [], share: row.volume_share_pct }
      current.rows.push(row); if (row.volume_share_pct != null) current.share = row.volume_share_pct; map.set(row.org_id, current)
    }
    return [...map.entries()]
  }, [quota])
  return (
    <div className="space-y-6">
      <SystemFaultsTab />
      <MonitorReleaseAlerts />
      <section className="rounded-xl p-4" style={{ background: '#1a1d27', border: '1px solid #2d3148' }}>
        <h2 className="text-sm font-semibold">טריות מדדים שעתיים</h2>
        <p className="text-xs mt-2" style={{ color: metricStale ? '#fbbf24' : '#34d399' }}>{metricStale ? 'המדדים חסרים או מיושנים (מעל 90 דקות)' : `עדכני · ${agoMinutes(newestMetric ? new Date(newestMetric).toISOString() : null)}`}</p>
      </section>
      <section className="space-y-3">
        <h2 className="text-sm font-semibold">התראות שירות</h2>
        {alerts.map((alert) => {
          const commercial = alert.payload?.kind === 'capacity_insight' || /^(quota|forecast|volume):/.test(alert.alert_key)
          const impact = typeof alert.payload?.impact === 'string' ? alert.payload.impact : null
          const check = typeof alert.payload?.what_to_check === 'string' ? alert.payload.what_to_check : null
          const actions = Array.isArray(alert.payload?.recommended_actions)
            ? alert.payload.recommended_actions.filter((item): item is string => typeof item === 'string')
            : []
          return (
            <article key={alert.id} className="rounded-xl p-4" style={{ background: '#1a1d27', border: '1px solid #2d3148' }}>
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold">{alert.title}</h3>
                <span className="text-[11px]" style={{ color: commercial ? '#60a5fa' : '#fbbf24' }}>
                  {commercial ? 'מידע מסחרי · ללא הודעה' : alert.severity}
                </span>
              </div>
              <p className="text-xs mt-2 whitespace-pre-wrap" style={{ color: '#d1d5db' }}>{alert.body}</p>
              {impact && <p className="text-xs mt-2">השפעה: {impact}</p>}
              {check && <p className="text-xs mt-1">בדיקה: {check}</p>}
              {actions.length > 0 && <p className="text-xs mt-1">טיפול: {actions.join(' · ')}</p>}
              <p className="text-[11px] mt-2" style={{ color: '#6b7280' }}>
                {alert.status === 'resolved' ? `נפתר ${new Date(alert.resolved_at ?? alert.last_seen).toLocaleString('he-IL')} · משך תקלה ${fmtDuration(Math.max(0, (Date.parse(alert.resolved_at ?? alert.last_seen) - Date.parse(alert.first_seen)) / 1000))}` : 'פתוח'}
              </p>
            </article>
          )
        })}
      </section>
      <section className="rounded-xl p-4 overflow-x-auto" style={{ background: '#1a1d27', border: '1px solid #2d3148' }}>
        <h2 className="text-sm font-semibold mb-3">מכסות לפי ארגון</h2>
        <table className="w-full min-w-[720px] text-xs text-right"><thead style={{ color: '#6b7280' }}><tr>{['ארגון', 'אנשי קשר', 'מכשירי WA', 'AI credits', 'נתח DB', 'תחזית', 'סטטוס'].map((h) => <th key={h} className="px-2 py-2">{h}</th>)}</tr></thead><tbody>
          {quotaByOrg.slice(0, 40).map(([orgId, org]) => {
            const byMetric = Object.fromEntries(org.rows.map((row) => [row.metric, row]))
            const badge = org.rows.some((row) => (row.pct ?? 0) >= 1 || (row.volume_share_pct ?? 0) > .4) ? 'חריגה' : org.rows.some((row) => (row.pct ?? 0) >= .8 || (row.days_to_limit != null && row.days_to_limit >= 0 && row.days_to_limit < 14)) ? 'אזהרה' : 'תקין'
            const cell = (key: string) => { const row = byMetric[key]; return row ? `${row.used}${row.effective_limit != null ? `/${row.effective_limit}` : ''}${row.pct != null ? ` (${Math.round(row.pct * 100)}%)` : ''}` : '—' }
            const soonest = org.rows.map((row) => row.days_to_limit).filter((v): v is number => v != null && v >= 0).sort((a, b) => a - b)[0]
            return <tr key={orgId} style={{ borderTop: '1px solid #2d3148' }}><td className="px-2 py-2">{org.name}</td><td className="px-2 py-2 font-mono">{cell('contacts')}</td><td className="px-2 py-2 font-mono">{cell('whatsapp_devices')}</td><td className="px-2 py-2 font-mono">{cell('ai_credits_monthly')}</td><td className="px-2 py-2">{org.share != null ? `${Math.round(org.share * 1000) / 10}%` : '—'}</td><td className="px-2 py-2">{soonest != null ? `בעוד ${Math.round(soonest)} ימים` : '—'}</td><td className="px-2 py-2"><span className="rounded px-2 py-1" style={{ background: badge === 'חריגה' ? '#ef444420' : badge === 'אזהרה' ? '#eab30820' : '#22c55e20' }}>{badge}</span></td></tr>
          })}
        </tbody></table>
      </section>
      <section className="rounded-xl p-4" style={{ background: '#1a1d27', border: '1px solid #2d3148' }}><h2 className="text-sm font-semibold mb-3">מפת השפעה · פיצ׳ר → שירות</h2><div className="space-y-2">{FEATURE_IMPACT.map((row) => <div key={row.feature} className="flex flex-wrap gap-2 text-xs"><span className="min-w-[150px]">{row.feature}</span>{row.services.map((service) => <span key={service} className="rounded px-2 py-1" style={{ background: '#252836' }}>{service}</span>)}</div>)}</div></section>
      {cron.length > 0 && <p className="text-[11px]" style={{ color: '#6b7280' }}>נתוני Cron מוצגים בלשונית Cron, כולל העמודה "מה מושפע".</p>}
    </div>
  )
}

function Drawer({ code, snapshot, onClose }: { code: string; snapshot: MonitorSnapshot; onClose: () => void }) {
  const bucket = code === 'platform' ? snapshot.platform : code === 'unmapped' ? snapshot.unmapped : null
  const efs = bucket?.efs ?? (snapshot.efs ?? []).filter((row) => row.module_code === code)
  const crons = bucket?.crons ?? (snapshot.cron ?? []).filter((row) => row.module_code === code)
  const integrations = (snapshot.integrations ?? []).filter((row) => row.module_code === code)
  const checks = (snapshot.checks ?? []).filter((row) => row.module_code === code)
  const title = code === 'platform' ? 'ליבה' : code === 'unmapped' ? 'לא משויך' : code
  const moduleRow = (snapshot.modules ?? []).find((row) => row.module_code === code)
  const lastFaultTitle = bucket?.last_fault_title || moduleRow?.last_fault_title || moduleRow?.last_fault || '—'
  const lastFaultAt = bucket?.last_fault_at || moduleRow?.last_fault_at
  return (
    <div className="fixed inset-0 z-50 flex justify-start" style={{ background: '#00000088' }} onClick={onClose}>
      <aside
        className="h-full w-full max-w-md overflow-y-auto p-4"
        style={{ background: '#1a1d27' }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold">{title}</h2>
          <button type="button" className="min-h-10 px-3 text-sm" onClick={onClose}>סגור</button>
        </div>
        <section className="mb-4 rounded-lg p-3" style={{ background: '#252836' }}><h3 className="text-xs mb-1" style={{ color: '#6b7280' }}>תקלה אחרונה</h3><p className="text-xs">{lastFaultTitle}</p><p className="text-[11px] mt-1" style={{ color: '#9ca3af' }}>{lastFaultAt ? agoMinutes(lastFaultAt) : '—'}</p></section>
        <DrawerList title="פונקציות" rows={efs.map((row) => ({
          key: componentName(row),
          label: componentName(row),
          status: row.status,
          evidence: `קריאות ${row.invocations_1h ?? '—'} · שגיאה ${efTopError(row) ?? '—'} · verify_jwt ${row.verify_jwt == null ? '—' : row.verify_jwt ? 'כן' : 'לא'}`,
          href: efLogUrl(row),
        }))} />
        <DrawerList title="Cron" rows={crons.map((row) => ({
          key: componentName(row),
          label: componentName(row),
          status: row.status,
          evidence: evidenceOf(row),
          href: null,
        }))} />
        <DrawerList title="אינטגרציות" rows={integrations.map((row) => ({
          key: componentName(row),
          label: componentName(row),
          status: row.status,
          evidence: evidenceOf(row),
          href: null,
        }))} />
        <DrawerList title="בדיקות" rows={checks.map((row) => ({
          key: row.key ?? '',
          label: row.title_he || row.key || '',
          status: row.status,
          evidence: row.evidence || '—',
          href: null,
        }))} />
      </aside>
    </div>
  )
}

function DrawerList({ title, rows }: { title: string; rows: Array<{ key: string; label: string; status?: MonitorStatus; evidence: string; href: string | null }> }) {
  if (rows.length === 0) return null
  return (
    <section className="mb-4">
      <h3 className="text-xs mb-2" style={{ color: '#6b7280' }}>{title}</h3>
      <ul className="space-y-2">
        {rows.map((row) => (
          <li key={row.key} className="text-xs">
            <div className="flex items-center justify-between gap-2">
              <span>{row.label}</span>
              <StatusPill status={row.status} />
            </div>
            <p style={{ color: '#9ca3af' }}>{row.evidence}</p>
            {row.href && <a className="underline" href={row.href} target="_blank" rel="noreferrer">לוג בסופאבייס</a>}
          </li>
        ))}
      </ul>
    </section>
  )
}
